import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchUsersApiThunk,
  addUserApiThunk,
  updateUserApiThunk,
  checkUserPermissionApiThunk,
  resetAllApiStatus,
  clearAPIStatusMessageFromState,
  deleteUserApiThunk,
  fetchUsersByIDApiThunk,
  fetchUserMetadataApiThunk,
  selectUserLanguageState,
  clearUsersNextToken,
} from './usersSlice';

import {
  CheckUserPermissionsRequest,
  CreateUserRequest,
  DeleteUserRequest,
  GetUsersRequest,
  GetUserMetadataRequest,
  ListUsersRequest,
  UpdateUserRequest,
} from 'src/types/orbit/User';
import { RootState } from '..';

export const useUsers = () => {
  const dispatch = useAppDispatch();
  const usersList = useAppSelector((state: RootState) => state.users.users);
  const fetchUsersApiStatus = useAppSelector(
    (state: RootState) => state.users.fetchUsersApiStatus,
  );
  const updateUserApiStatus = useAppSelector(
    (state: RootState) => state.users.updateUserApiStatus,
  );
  const deleteUserApiStatus = useAppSelector(
    (state: RootState) => state.users.deleteUserApiStatus,
  );
  const checkUserPermissionApiStatus = useAppSelector(
    (state: RootState) => state.users.checkUserPermissionApiStatus,
  );
  const userPermission = useAppSelector(
    (state: RootState) => state.users.checkUserPermissionResponse,
  );
  const fetchUserByUserIdApiStatus = useAppSelector(
    (state: RootState) => state.users.fetchUserByUserIdApiStatus,
  );
  const fetchUserMetadataApiStatus = useAppSelector(
    (state: RootState) => state.users.fetchUserMetadataApiStatus,
  );
  const currentUser = useAppSelector((state: RootState) => state.users.currentUser);
  const currentUserMetadata = useAppSelector(
    (state: RootState) => state.users.currentUserMetadata,
  );
  const userSelectedLanguage = useAppSelector(
    (state: RootState) => state.users.userSelectedLanguage,
  );

  const loadUsers = useCallback(
    (data: ListUsersRequest) => {
      dispatch(fetchUsersApiThunk(data));
    },
    [dispatch],
  );

  const getUserByUserId = useCallback(
    async (data: GetUsersRequest) => {
      dispatch(fetchUsersByIDApiThunk(data));
    },
    [dispatch],
  );

  const getUserMetadata = useCallback(
    async (data: GetUserMetadataRequest) => {
      dispatch(fetchUserMetadataApiThunk(data));
    },
    [dispatch],
  );

  const createUser = useCallback(
    async (userData: CreateUserRequest) => {
      dispatch(addUserApiThunk(userData));
    },
    [dispatch],
  );

  const updateUser = useCallback(
    async (userData: UpdateUserRequest) => {
      dispatch(updateUserApiThunk({ data: userData }));
    },
    [dispatch],
  );

  const selectUserLanguage = useCallback(
    async (language: string) => {
      dispatch(selectUserLanguageState(language));
    },
    [dispatch],
  );

  const deleteUser = useCallback(
    async (userData: DeleteUserRequest) => {
      dispatch(deleteUserApiThunk({ data: userData }));
    },
    [dispatch],
  );

  const resetApiStatus = useCallback(() => {
    dispatch(resetAllApiStatus());
  }, [dispatch]);

  const clearAPIStatusMessage = useCallback(() => {
    dispatch(clearAPIStatusMessageFromState());
  }, [dispatch]);

  const checkUserPermissions = useCallback(
    async (data: CheckUserPermissionsRequest) => {
      dispatch(checkUserPermissionApiThunk(data));
    },
    [dispatch],
  );

  const clearNextToken = useCallback(() => {
    dispatch(clearUsersNextToken());
  }, [dispatch]);

  return {
    usersList,
    userPermission,
    fetchUsersApiStatus,
    updateUserApiStatus,
    checkUserPermissionApiStatus,
    deleteUserApiStatus,
    currentUser,
    currentUserMetadata,
    fetchUserByUserIdApiStatus,
    fetchUserMetadataApiStatus,
    loadUsers,
    createUser,
    updateUser,
    deleteUser,
    checkUserPermissions,
    resetApiStatus,
    clearAPIStatusMessage,
    getUserByUserId,
    getUserMetadata,
    selectUserLanguage,
    userSelectedLanguage,
    clearNextToken,
  };
};
